import { z } from 'zod'

/**
 * PKCE for every Google Sheets connect start. Shared by the onboarding and
 * dashboard flows so neither can leave Google without a verifier; each flow's
 * own session store still owns its nonce and uid binding.
 */
const KEY = 'google_oauth_pkce'
const record = z.object({ nonce: z.string().min(1), verifier: z.string().min(43) })

const base64Url = (bytes: Uint8Array) =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

/** Store a fresh verifier for this nonce and return its S256 code_challenge. */
export async function beginGooglePkce(nonce: string): Promise<string> {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)))
  // Throws on a full/unavailable store, like the session stores it accompanies.
  sessionStorage.setItem(KEY, JSON.stringify(record.parse({ nonce, verifier })))
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64Url(new Uint8Array(digest))
}

/** One use only. A different nonce leaves the pending start intact. */
export function takeGooglePkceVerifier(nonce: string): string | null {
  try {
    const result = record.safeParse(JSON.parse(sessionStorage.getItem(KEY) || 'null'))
    if (!result.success || result.data.nonce !== nonce) return null
    sessionStorage.removeItem(KEY)
    return result.data.verifier
  } catch {
    return null
  }
}
